import { useTextField } from '@react-aria/textfield';
import pick from 'lodash/pick';
import { FocusEvent, ReactElement, useId, useRef, useState } from 'react';

import { CircleX as Fail, CircleCheck as Success } from '@calm-web/icons';

import { useAccessibleOnChange } from '../../utils/useAccessibleOnChange';
import {
	BackgroundWrapper,
	DefaultCustomInputProps,
	ErrorContainer,
	IconWrapper,
	Input,
	Label,
	ValidationImage,
	Wrapper,
} from './styles';
import { FormInputProps } from './types';

type CustomizableInputProps<T> = Omit<FormInputProps, '$UNSAFE_showFocus' | '$UNSAFE_hasBlurred'> & {
	UNSAFE_showFocus?: boolean;
	UNSAFE_hasBlurred?: boolean;
	CustomInputProps: T;
	CustomInput: React.FC<DefaultCustomInputProps & { ref?: Parameters<typeof Input>[0]['ref'] } & T>;
	customInputIsTextArea?: boolean;
	errorOnDarkBackground?: boolean;
};

const defaultOnChange = (): void => {};

export const CustomizableInput = (props: CustomizableInputProps<{}>): ReactElement => {
	const {
		name,
		value,
		type = 'text',
		onChange = defaultOnChange,
		onBlur,
		onFocus,
		isValid: _isValid,
		noValidation,
		Icon,
		iconColor,
		className,
		error: _error,
		errors,
		showValidation: _showValidation,
		onClick,
		LabelComponent,
		label,
		placeholder,
		disabled,
		readOnly,
		CustomInput,
		CustomInputProps,
		backgroundColor = 'white',
		activeBackgroundColor,
		filledBackgroundColor,
		textColor,
		borderRadius,
		borderWidth,
		borderColor,
		activeBorderColor,
		filledBorderColor,
		placeholderTextColor,
		labelFocusScaleTransform,
		customInputIsTextArea = false,
		noMargin = false,
		required = false,
		errorOnDarkBackground = false,
		UNSAFE_showFocus = false,
		UNSAFE_hasBlurred = false,
		isCenteredText,
		isLargerInput,
	} = props;
	const isValid = _isValid ?? true;
	const error = _error ?? errors?.[0];
	const isTypeDate = type === 'date';

	const ref = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
	const [hasBlurred, setHasBlurred] = useState(UNSAFE_hasBlurred);
	const [isFocused, setIsFocused] = useState(UNSAFE_showFocus);

	const handleChange = useAccessibleOnChange(onChange, name, type, ref);

	const handleFocus = ((e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		setIsFocused(true);
		if (onFocus) {
			onFocus(e);
		}
		// TODO: figure out a way to avoid casting here
	}) as (e: FocusEvent) => void;

	const handleBlur = ((e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		setIsFocused(false);
		if (!readOnly && !disabled) {
			setHasBlurred(true);
		}
		if (onBlur) {
			onBlur(e);
		}
	}) as (e: FocusEvent) => void;

	const isEmpty = value.length === 0;
	// TODO: useTextField doesn't accept these, but useNumberField would...
	const minMaxStep = pick(props, ['min', 'max', 'step']);
	const showValidation = _showValidation ?? (hasBlurred && !noValidation && typeof _isValid !== 'undefined');
	const showErrorMessage = showValidation && !isValid && error;
	const hidePlaceholder = Boolean(!placeholder && !LabelComponent && !props['aria-label']);
	const hasDefaultLabelAndPlaceholder = Boolean(label && !LabelComponent && placeholder);
	const provideSpaceForAnimatedLabel = hidePlaceholder || hasDefaultLabelAndPlaceholder;
	const errorMessageId = useId();

	const { labelProps, inputProps, errorMessageProps } = useTextField(
		{
			inputElementType: customInputIsTextArea ? 'textarea' : 'input',
			...props,
			isDisabled: disabled,
			isReadOnly: readOnly,
			isRequired: required,
			...(noValidation ? {} : { validationState: isValid ? 'valid' : 'invalid' }),
			...(showErrorMessage ? { errorMessge: error, ['aria-errormessage']: errorMessageId } : {}),
			onChange: handleChange,
			onBlur: handleBlur,
			onFocus: handleFocus,
		},
		ref,
	);

	return (
		<>
			{LabelComponent ? <LabelComponent {...{ ...labelProps, children: label }} /> : null}
			<Wrapper className={className} isEmpty={isEmpty} onClick={onClick} $noMargin={noMargin}>
				<BackgroundWrapper
					$backgroundColor={backgroundColor}
					$disabled={inputProps.disabled}
					$borderRadius={borderRadius}
				>
					{Icon && (
						<IconWrapper iconColor={iconColor}>
							<Icon />
						</IconWrapper>
					)}
					<CustomInput
						$UNSAFE_showFocus={UNSAFE_showFocus}
						$hasIcon={!!Icon}
						$hidePlaceholder={hidePlaceholder}
						$isEmpty={isEmpty}
						$provideSpaceForAnimatedLabel={provideSpaceForAnimatedLabel}
						{...(showValidation ? { $isValid: isValid, $isError: !isValid } : {})}
						$validationShown={showValidation}
						{...inputProps}
						{...minMaxStep}
						{...CustomInputProps}
						aria-invalid={showErrorMessage ? 'true' : 'false'}
						aria-errormessage={showErrorMessage ? errorMessageId : undefined}
						aria-describedby={showErrorMessage ? errorMessageId : undefined}
						ref={ref}
						required={required}
						$activeBackgroundColor={activeBackgroundColor}
						$filledBackgroundColor={filledBackgroundColor}
						$textColor={textColor}
						$borderRadius={borderRadius}
						$borderWidth={borderWidth}
						$borderColor={borderColor}
						$activeBorderColor={activeBorderColor}
						$filledBorderColor={filledBorderColor}
						$placeholderTextColor={placeholderTextColor}
						$isCenteredText={isCenteredText}
						$isLargerInput={isLargerInput}
						$isTypeDate={isTypeDate}
					/>
					{label && !LabelComponent && (
						<Label
							$isDisabled={inputProps.disabled}
							$isFocusedOrHasValue={isFocused || !isEmpty || !!placeholder}
							$hasIcon={!!Icon}
							$textColor={textColor}
							$labelFocusScaleTransform={labelFocusScaleTransform}
							{...labelProps}
						>
							{label}
						</Label>
					)}
					{showValidation && (
						<ValidationImage isVisible={showValidation} isValid={isValid}>
							{isValid ? <Success /> : <Fail />}
						</ValidationImage>
					)}
				</BackgroundWrapper>
				{showErrorMessage && (
					<ErrorContainer
						{...errorMessageProps}
						id={errorMessageId}
						role="alert"
						aria-live="polite"
						$hasDarkBackground={errorOnDarkBackground}
						$isCenteredText={isCenteredText}
					>
						{error}
					</ErrorContainer>
				)}
			</Wrapper>
		</>
	);
};

// This is separated from CustomizableInput so that TextArea can provide a <textarea /> instead of an <input />
const FormInput = ({
	$UNSAFE_showFocus: UNSAFE_showFocus,
	$UNSAFE_hasBlurred: UNSAFE_hasBlurred,
	...props
}: FormInputProps): ReactElement => {
	return (
		<CustomizableInput
			{...props}
			CustomInput={Input}
			CustomInputProps={{}}
			UNSAFE_showFocus={UNSAFE_showFocus}
			UNSAFE_hasBlurred={UNSAFE_hasBlurred}
		/>
	);
};

export default FormInput;
